import { registerThemeEditorSection } from "@stardust/theme-editor";
import Slider from "@stardust/slider";

class CollectionSliderSection {
  constructor(element) {
    this.element = element;
    this.tabs = this.element.querySelectorAll("[data-tab-button]");
    this.tabContents = this.element.querySelectorAll("[data-tab-content]");
    this.sliders = this.element.querySelectorAll("[data-collection-slider]");
    this.tabSliders = [];
    this.searchspringLoaded = new Map(); // Track which Searchspring tabs have been loaded
    this.searchspringLoading = new Map(); // Track which tabs are currently loading
    this.load();
  }

  load() {
    // Early return if no sliders
    if (!this.sliders.length) return;

    // Register tab button clicks first - makes section interactive
    if (this.tabs.length > 0) {
      this.tabs.forEach((tab, index) => {
        tab.addEventListener("click", () => {
          this.switchTab(tab, index);
        });
      });

      if (this.tabs.length === 1) {
        // Single-tab: button is always display:none (no tab UI needed) — activate unconditionally
        this.switchTab(this.tabs[0], 0);
      } else {
        // Multi-tab: skip buttons hidden server-side due to empty collections
        const firstVisibleIndex = [...this.tabs].findIndex((t) => t.style.display !== "none");
        if (firstVisibleIndex === -1) {
          const section = this.element.closest("section");
          if (section) section.style.display = "none";
          return;
        }
        this.switchTab(this.tabs[firstVisibleIndex], firstVisibleIndex);
      }
    }

    // Then initialize sliders (can complete async)
    this.sliders.forEach((sliderEl) => {
      // Mark as active for CSS controls visibility
      sliderEl.classList.add("is-active");
      const slider = new Slider(sliderEl, { snap: false, snapOnRelease: true });
      this.tabSliders.push(slider);
    });
  }

  async switchTab(tab, index) {
    if (tab.classList.contains("tab-active")) return;

    this.tabs.forEach((t) => {
      t.classList.remove("tab-active");
      t.setAttribute("aria-selected", "false");
    });
    tab.classList.add("tab-active");
    tab.setAttribute("aria-selected", "true");

    this.tabContents.forEach((content) => {
      content.style.display = "none";
      content.setAttribute("aria-hidden", "true");
    });

    if (this.tabContents[index]) {
      this.tabContents[index].style.display = "block";
      this.tabContents[index].setAttribute("aria-hidden", "false");
    }

    // Check if this is a Searchspring tab that needs loading
    const tabContent = this.tabContents[index];
    const blockType = tab.dataset.blockType;

    if (blockType === "searchspring" && !this.searchspringLoaded.get(index) && !this.searchspringLoading.get(index)) {
      await this.loadSearchspringTab(tabContent, index);
    }

    const slider = this.tabSliders[index];
    if (slider) {
      requestAnimationFrame(() => slider.updateControls());
    }
  }

  /**
   * Hide a tab button and its panel when it has no content to show.
   * Switches to the first remaining visible tab if the collapsed tab was active.
   * Hides the entire section if no visible tabs remain.
   * @param {number} tabIndex
   */
  #collapseTab(tabIndex) {
    const tab = this.tabs[tabIndex];
    const content = this.tabContents[tabIndex];

    if (tab) tab.style.display = "none";
    if (content) {
      content.style.display = "none";
      content.setAttribute("aria-hidden", "true");
    }

    const visibleTabs = [...this.tabs].filter((t) => t.style.display !== "none");

    if (visibleTabs.length === 0) {
      const section = this.element.closest("section");
      if (section) section.style.display = "none";
      return;
    }

    if (tab?.classList.contains("tab-active")) {
      const nextTab = visibleTabs[0];
      const nextIndex = [...this.tabs].indexOf(nextTab);
      this.switchTab(nextTab, nextIndex);
    }
  }

  /**
   * Load Searchspring recommendations for a tab
   * @param {HTMLElement} tabContent - The tab content container
   * @param {number} tabIndex - The tab index
   */
  async loadSearchspringTab(tabContent, tabIndex) {
    // Mark as loading to prevent duplicate requests
    this.searchspringLoading.set(tabIndex, true);

    const tag = tabContent.dataset.searchspringTag;
    const limit = parseInt(tabContent.dataset.searchspringLimit || "6", 10);
    const mobileLimit = parseInt(tabContent.dataset.searchspringMobileLimit || "4", 10);
    const sku = document.querySelector("[data-product-sku]")?.textContent || null;

    // Hoist both refs so catch can clear them on failure — otherwise mobile skeletons
    // pulse forever since the grid is visible and the slider is hidden below 641px
    const sliderList = tabContent.querySelector("[data-slider-list]");
    const mobileGridInner = tabContent.querySelector("[data-searchspring-mobile-grid-inner]");

    if (!tag) {
      console.warn("[collection-slider] No Searchspring tag found for tab", tabIndex);
      this.searchspringLoading.set(tabIndex, false);
      return;
    }

    try {
      let result;

      if (tag === "trending") {
        const { fetchTrendingProducts } = await import("@stardust/searchspring-trending");
        result = await fetchTrendingProducts(limit);
      } else {
        const { getRecommendations } = await import("@stardust/searchspring-recommended");
        result = await getRecommendations({ tag, sku, limit });
      }

      const products = result.products || [];
      const cardBuilder = result._cardBuilder;

      if (!sliderList) {
        this.searchspringLoaded.set(tabIndex, true);
        return;
      }

      sliderList.innerHTML = "";
      if (mobileGridInner) mobileGridInner.innerHTML = "";

      if (products.length > 0) {
        products.forEach((product, i) => {
          const slideWrapper = document.createElement("div");
          // Width comes from the section's Card size settings (slider-size.css).
          slideWrapper.dataset.sliderItem = "";
          slideWrapper.appendChild(cardBuilder(product));
          sliderList.appendChild(slideWrapper);

          if (mobileGridInner && i < mobileLimit) {
            mobileGridInner.appendChild(cardBuilder(product));
          }
        });

        // Refresh slider after dynamic products replace skeletons
        const slider = this.tabSliders[tabIndex];
        if (slider) {
          requestAnimationFrame(() => slider.refresh());
        }
      } else {
        this.#collapseTab(tabIndex);
      }

      this.searchspringLoaded.set(tabIndex, true);
    } catch (error) {
      console.error("[collection-slider] Failed to load Searchspring recommendations:", error);
      this.#collapseTab(tabIndex);
    } finally {
      this.searchspringLoading.set(tabIndex, false);
    }
  }

  unload() {
    this.tabSliders.forEach((slider) => {
      if (slider) {
        slider.destroy();
      }
    });
    this.tabSliders = [];
    this.searchspringLoaded.clear();
    this.searchspringLoading.clear();
  }
}

registerThemeEditorSection("collection-slider-section", CollectionSliderSection);
